<script>
export default {
  name: "DidoCatalog",
};
</script>

<script setup>
import { ref, provide, computed, watch } from "vue";
import DidoExplore from "./Explore.vue";
import DidoDatafile from "./Datafile.vue";
import DidoDataset from "./Dataset.vue";
import DidoApidoc from "./Apidoc.vue";
import DidoCatalog404 from "./catalog/404.vue";
import DidoCatalog500 from "./catalog/500.vue";
import DidoCatalogBreadcrumb from "./catalog/Breadcrumb.vue";
import DidoCatalogMessageService from "./catalog/MessageService.vue";
import api from "../api";
import {
  createCatalogExploreFilterFromUrl,
  createCatalogUrlFromExploreFilter,
  exploreUrlOnLabelCard,
  pause,
} from "../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  catalogContactUrl: {
    type: String,
    required: false,
    default: "https://statistiques.developpement-durable.gouv.fr/contact",
  },
  linkedDocsApiUrl: { type: String, required: false, default: void 0 },
  websitePage: { type: String, required: false, default: void 0 },
  landingPage: { type: String, required: false, default: void 0 },
  initialExploreUrl: { type: String, required: false, default: "" },
  messageService: { type: Boolean, required: false, default: false },
  messageServiceType: { type: String, required: false, default: "primary" },
  messageServicePermanent: { type: Boolean, required: false, default: false },
});

// provide apiUrl for subcomponents
provide("apiUrl", props.apiUrl);

// define events emitted
const emit = defineEmits(["bad-configuration"]);

// define reactive data for loading flag
const loading = ref(false);

// create a computed flag to mark the widget as configured
const configured = computed(() => !!props.apiUrl);

// Management of appVersion from package.json
const appVersion = `v${import.meta.env.PACKAGE_VERSION}`;
// Management of apiVersion: create reactive data with value from api
const apiVersion = ref("");
api.getApiVersion(props.apiUrl).then((version) => (apiVersion.value = version));

// create refs for dom node of breadcrumb
const breadcrumbNode = ref(null);

// ******Management of current route*******
// create a reactive data for current route
const currentRoute = ref({ page: null });
// method for updating current route from url
const setCurrentRouteFromUrl = () => {
  // reset currentRoute
  currentRoute.value = {
    page: undefined,
    message404: undefined,
    exploreFilter: undefined,
    datasetId: undefined,
    datafileRid: undefined,
    datafileMillesime: undefined,
    selectedTab: undefined,
    lastExploreUrl: currentRoute.value.lastExploreUrl || "?page=explore",
  };

  // if widget bad configured
  if (!configured.value) {
    emit("bad-configuration");
    return;
  }

  // get url
  const url = new URL(window.location.href);

  // set route page from url
  currentRoute.value.page = url.searchParams.get("page") || "explore";

  // if page is not "apidoc", "explore", "dataset" or "datafile" set 404
  if (
    !["apidoc", "explore", "dataset", "datafile"].includes(
      currentRoute.value.page,
    )
  ) {
    currentRoute.value.page = "404";
    currentRoute.value.message404 = "Page demandée inexistante";
    return;
  }

  // add info to next route from url
  if (currentRoute.value.page === "explore") {
    currentRoute.value.exploreFilter = createCatalogExploreFilterFromUrl(url);
    currentRoute.value.lastExploreUrl = createCatalogUrlFromExploreFilter(
      currentRoute.value.exploreFilter,
    );
  }
  if (currentRoute.value.page === "dataset") {
    currentRoute.value.datasetId = url.searchParams.get("datasetId") || "";
    currentRoute.value.selectedTab = url.searchParams.get("tab") || "datafiles";
  }
  if (currentRoute.value.page === "datafile") {
    currentRoute.value.datafileRid = url.searchParams.get("datafileRid") || "";
    currentRoute.value.datafileMillesime =
      url.searchParams.get("datafileMillesime") || "";
    currentRoute.value.selectedTab = url.searchParams.get("tab") || "datas";
  }
};
// when history changes: update route from new Url
window.addEventListener("popstate", () => setCurrentRouteFromUrl());
// provide method for updating current route in child components
provide("setCurrentRouteFromUrl", setCurrentRouteFromUrl);

// *********************
// define events
// *********************
// on breadcrumb click
const onBreadcrumbClick = ({ page, url }) => goToUrl({ page, url });
// click on apidoc link
const onApidocRoute = () => {
  goToUrl({ page: "DidoApidoc", url: "?page=apidoc" });
};
// when the explore filter change (card type, facet selection, search by text, ordering)
const onNewExploreFilter = (filter) => {
  goToUrl({
    page: "DidoExplore",
    url: createCatalogUrlFromExploreFilter(filter),
  });
};
// click on a dataset or a datafile card
const onCardClicked = ({ type, datasetId = null, datafileRid = null }) => {
  if (type === "dataset")
    goToUrl({
      page: "DidoDatasetDetail",
      url: `?page=dataset&datasetId=${datasetId}&tab=datafiles`,
    });
  if (type === "datafile")
    goToUrl({
      page: "DidoDatafileDetail",
      url: `?page=datafile&datafileRid=${datafileRid}&tab=datas`,
    });
};
// click on a chip in a dataset or a datafile card
const onLabelClickedOnCard = ({ card, type, value }) => {
  goToUrl({
    page: "DidoExplore",
    url: exploreUrlOnLabelCard(card, type, value),
  });
};
// click on a tab resources in dataset pages
const onTabChangedInDataset = ({ tab, datasetId }) => {
  goToUrl({
    page: "DidoDatasetDetail",
    url: `?page=dataset&datasetId=${datasetId}&tab=${tab}`,
  });
};
// click on the parent dataset link in the datafile page
const onDatasetSelected = ({ datasetId }) => {
  goToUrl({
    page: "DidoDatasetDetail",
    url: `?page=dataset&datasetId=${datasetId}&tab=datafiles`,
  });
};
// click on a datafile child card in the dataset page
const onDatafileSelected = ({ datafileRid }) => {
  goToUrl({
    page: "DidoDatafileDetail",
    url: `?page=datafile&datafileRid=${datafileRid}&tab=datas`,
  });
};
// click on a specific millesime in the datafile page
const onMillesimeSelected = ({ datafileRid, datafileMillesime }) => {
  goToUrl({
    page: "DidoDatafileDetail",
    url: `?page=datafile&datafileRid=${datafileRid}&datafileMillesime=${datafileMillesime}&tab=datas`,
  });
};
// click on a tab in millesime of datafile page
const onTabChangedInMillesime = ({ tab, datafileRid, datafileMillesime }) => {
  goToUrl({
    page: "DidoDatafileDetail",
    url: `?page=datafile&datafileRid=${datafileRid}&datafileMillesime=${datafileMillesime}&tab=${tab}`,
  });
};
// no datafile with the datafileRid
const onDatafile404 = ({ datafileRid }) => {
  currentRoute.value.page = "404";
  currentRoute.value.message404 = `Le fichier de données avec le rid ${datafileRid} n'existe pas`;
};
// no dataset with the datasetId
const onDataset404 = ({ datasetId }) => {
  currentRoute.value.page = "404";
  currentRoute.value.message404 = `Le jeu de données avec l'id ${datasetId} n'existe pas`;
};
// server error
const onServerError = () => {
  currentRoute.value.page = "500";
};

// reactive data for required data for page
const pageInfos = ref({});
// initialization of widget: get datas required for current page
const getPageDatas = async () => {
  if (
    !["explore", "dataset", "datafile", "apidoc", "404", "500"].includes(
      currentRoute.value.page,
    )
  ) {
    pageInfos.value = {};
    return;
  }

  if (currentRoute.value.page === "explore") {
    pageInfos.value = {
      page: "explore",
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
    return;
  }

  if (currentRoute.value.page === "dataset") {
    if (!pageInfos.value.dataset) {
      loading.value = true;
      pageInfos.value.dataset = await api
        .getDataset(props.apiUrl, currentRoute.value.datasetId)
        .then(({ status, dataset }) => {
          if (status !== "available") throw new Error(status);
          return dataset;
        })
        .catch((error) => {
          if (error.message === "notFound")
            return onDataset404({ datasetId: currentRoute.value.datasetId });
          return onServerError();
        })
        .finally(() => (loading.value = false));
    }
    pageInfos.value = {
      page: "dataset",
      dataset: pageInfos.value.dataset,
      datasetUrl: catalogUrl.value({
        type: "dataset",
        datasetId: pageInfos.value.dataset.id,
      }),
      selectedTab: currentRoute.value.selectedTab,
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
  }

  if (currentRoute.value.page === "datafile") {
    if (!pageInfos.value.datafile) {
      loading.value = true;
      pageInfos.value.datafile = await api
        .getDatafile(props.apiUrl, currentRoute.value.datafileRid)
        .then(({ status, datafile }) => {
          if (status !== "available") throw new Error(status);
          return datafile;
        })
        .catch((error) => {
          if (error.message === "notFound")
            return onDatafile404({
              datafileRid: currentRoute.value.datafileRid,
            });
          return onServerError();
        })
        .finally(() => (loading.value = false));
    }
    pageInfos.value = {
      page: "datafile",
      datafile: pageInfos.value.datafile,
      datasetUrl: catalogUrl.value({
        type: "dataset",
        datasetId: pageInfos.value.datafile.dataset.id,
      }),
      datafileUrl: catalogUrl.value({
        type: "datafile",
        datafileRid: pageInfos.value.datafile.rid,
      }),
      selectedTab: currentRoute.value.selectedTab,
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
  }

  if (currentRoute.value.page === "apidoc") {
    pageInfos.value = {
      page: "apidoc",
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
  }

  if (currentRoute.value.page === "404") {
    pageInfos.value = {
      page: "404",
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
  }

  if (currentRoute.value.page === "500") {
    pageInfos.value = {
      page: "500",
      exploreUrl: currentRoute.value.lastExploreUrl,
      websitePage: props.websitePage,
      landingPage: props.landingPage,
    };
  }
};
// define method to go to an url when click on breadcrumb link
const goToUrl = async ({ page, url }) => {
  window.history.pushState(null, page, url);
  setCurrentRouteFromUrl();
  await pause(1);
  breadcrumbNode.value?.$el.scrollIntoView({
    block: "center",
    behavior: "auto",
  });
};

// define watcher for current route
watch(
  () => currentRoute.value,
  () => getPageDatas(),
  { deep: true },
);

// provide to child components a flag to know if the root element is the catalog
provide("inCatalog", true);

// method for creation of an url catalog
const catalogUrl = computed(() => (args) => {
  const baseUrl = window.location.pathname;
  if (args.type === "apidoc") return `${baseUrl}?page=apidoc`;
  if (args.type === "contact") return props.catalogContactUrl;
  if (args.type === "api") return props.apiUrl;
  if (args.type === "explore")
    return `${baseUrl}${currentRoute.value.lastExploreUrl || "?page=explore"}`;
  if (args.type === "dataset")
    return `${baseUrl}?page=dataset&datasetId=${args.datasetId}`;
  if (args.type === "datafile")
    return `${baseUrl}?page=datafile&datafileRid=${args.datafileRid}`;
  if (args.type === "tag")
    return `${baseUrl}?page=explore&type=${args.card}&tags=${args.value}`;
  if (args.type === "topic")
    return `${baseUrl}?page=explore&type=${args.card}&topics=${args.value}`;
  if (args.type === "frequency")
    return `${baseUrl}?page=explore&type=${args.card}&frequencies=${args.value}`;
  if (args.type === "granularity")
    return `${baseUrl}?page=explore&type=${args.card}&granularities=${args.value}`;
  if (args.type === "zone")
    return `${baseUrl}?page=explore&type=${args.card}&zones=${args.value}`;
  if (args.type === "license")
    return `${baseUrl}?page=explore&type=${args.card}&licenses=${args.value}`;
  return null;
});

// provide to child components the method creating a catalog url
provide("catalogUrl", catalogUrl);

// initialization of widget: setting current route from url and get page datas
if (
  props.initialExploreUrl &&
  !new URL(window.location.href).searchParams.get("page")
)
  goToUrl({
    page: "DidoExplore",
    url: `?page=explore&${props.initialExploreUrl}`,
  });
else setCurrentRouteFromUrl();

// provide apiUrl for child components
provide("apiUrl", props.apiUrl);
</script>

<template>
  <div class="dido-widget-catalogue relative-position">
    <!-- Bad configuration of widget -->
    <div v-if="!configured">
      <div>Impossible de charger le composant...</div>
    </div>
    <div v-else class="dido-widget-catalogue-content relative-position">
      <template v-if="!loading">
        <div class="q-pa-md q-mb-sm row-start-center dido-breadcrumb">
          <DidoCatalogBreadcrumb
            v-if="pageInfos.page"
            ref="breadcrumbNode"
            :page-infos="pageInfos"
            @breadcrumb-click="onBreadcrumbClick"
          />
        </div>
        <DidoCatalogMessageService
          v-if="props.messageService"
          :type="props.messageServiceType"
          :dismissible="!props.messageServicePermanent"
        >
          <template #content>
            <slot name="messageServiceContent" />
          </template>
        </DidoCatalogMessageService>
        <DidoExplore
          v-if="pageInfos && pageInfos.page === 'explore'"
          :api-url="props.apiUrl"
          :explore-filter="currentRoute.exploreFilter"
          @click-card="onCardClicked"
          @click-apidoc="onApidocRoute"
          @label-selection-on-card="onLabelClickedOnCard"
          @new-filter="onNewExploreFilter"
        />
        <DidoDataset
          v-if="pageInfos.page === 'dataset'"
          :api-url="props.apiUrl"
          :dataset="pageInfos.dataset"
          :linked-docs-api-url="props.linkedDocsApiUrl"
          :selected-tab="pageInfos.selectedTab"
          @label-selection="
            onLabelClickedOnCard({ card: 'dataset', ...$event })
          "
          @datafile-selection="onDatafileSelected"
          @tab-changed-in-dataset="onTabChangedInDataset"
          @dataset-not-retrieved="onDataset404"
        />
        <DidoDatafile
          v-if="pageInfos.page === 'datafile'"
          :api-url="props.apiUrl"
          :datafile="pageInfos.datafile"
          :datafile-millesime="currentRoute.datafileMillesime"
          :millesime-selected-tab="pageInfos.selectedTab"
          :linked-docs-api-url="props.linkedDocsApiUrl"
          @label-selection="
            onLabelClickedOnCard({ card: 'datafile', ...$event })
          "
          @dataset-selection="onDatasetSelected"
          @millesime-selection="onMillesimeSelected"
          @tab-changed-in-millesime="onTabChangedInMillesime"
          @datafile-not-retrieved="onDatafile404"
        />
        <DidoApidoc v-if="pageInfos.page === 'apidoc'" />
        <DidoCatalog404
          v-if="pageInfos.page === '404'"
          :text-message="currentRoute.message404"
        />
        <DidoCatalog500 v-if="pageInfos.page === '500'" />
      </template>
      <!-- Spinner during interaction with api -->
      <QInnerLoading :showing="loading">
        <QSpinner size="50px" color="secondary" />
        <div class="text-h6 text-secondary">
          Chargement des données en cours
        </div>
      </QInnerLoading>
    </div>
    <div class="q-mt-md row-end-center">
      <span>
        <span class="text-italic">catalogue :</span>&nbsp;
        <span class="text-bold text-secondary">{{ appVersion }}</span>
      </span>
      <span class="q-ml-md">
        <span class="text-italic">api :</span>&nbsp;
        <span class="text-bold text-secondary">{{ apiVersion }}</span>
      </span>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-catalogue
  .dido-widget-catalogue-content
    min-height: 200px
    .dido-breadcrumb
      background-color: #f2f2f2
      font-family: Roboto
      :deep( .q-breadcrumbs__el)
        font-weight: 400
        font-size: 15.2px
      :deep( .q-breadcrumbs--last .q-breadcrumbs__el)
        font-weight: 700
</style>
